package com.iptvplayer.web.ui

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

fun StyleScope.ellipsis() {
    property("white-space", "nowrap")
    property("overflow", "hidden")
    property("text-overflow", "ellipsis")
}

@Composable
fun PageContainer(content: @Composable () -> Unit) {
    Div(attrs = {
        style { property("max-width", "1160px"); property("margin", "0 auto"); property("padding", "24px 22px 60px") }
    }) { content() }
}

@Composable
fun SectionTitle(text: String) {
    H3(attrs = { style { property("margin", "26px 0 12px"); property("font-size", "17px") } }) { Text(text) }
}

@Composable
fun MediaGrid(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(150px, 1fr))")
            property("gap", "16px")
        }
    }) { content() }
}

@Composable
fun TruncatedHint(shown: Int, total: Int) {
    Div(attrs = {
        style { property("color", Theme.textDim); property("font-size", "13px"); property("margin-top", "12px") }
    }) { Text("Showing first $shown of $total — use search or a category to narrow.") }
}

@Composable
fun EmptyState(text: String) {
    Div(attrs = {
        style {
            property("padding", "50px 20px"); property("text-align", "center")
            property("color", Theme.textDim); property("font-size", "15px")
        }
    }) { Text(text) }
}

/** A poster/logo card for a channel or VOD item. */
@Composable
fun MediaCard(name: String, imageUrl: String?, subtitle: String?, onClick: () -> Unit) {
    Div(attrs = {
        classes("card")
        onClick { onClick() }
        style {
            cardStyle(); property("cursor", "pointer"); property("overflow", "hidden")
        }
    }) {
        Div(attrs = {
            style {
                property("width", "100%"); property("aspect-ratio", "16 / 10"); property("background", Theme.surfaceAlt)
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); property("overflow", "hidden")
            }
        }) {
            if (!imageUrl.isNullOrBlank()) {
                Img(src = proxied(imageUrl), attrs = { attr("loading", "lazy"); attr("decoding", "async"); style { property("width", "100%"); property("height", "100%"); property("object-fit", "cover") } })
            } else {
                Span(attrs = { style { property("font-size", "30px"); property("color", Theme.textDim) } }) {
                    Text(name.take(1).uppercase())
                }
            }
        }
        Div(attrs = { style { property("padding", "9px 11px") } }) {
            Div(attrs = { style { property("font-size", "14px"); property("font-weight", "600"); ellipsis() } }) { Text(name) }
            if (!subtitle.isNullOrBlank()) {
                Div(attrs = { style { property("font-size", "12px"); property("color", Theme.textDim); property("margin-top", "2px"); ellipsis() } }) { Text(subtitle) }
            }
        }
    }
}

@Composable
fun Chip(text: String, active: Boolean, onClick: () -> Unit) {
    Button(attrs = {
        onClick { onClick() }
        style {
            property("background", if (active) Theme.primary else Theme.surface)
            property("color", if (active) "#ffffff" else Theme.text)
            property("border", "1px solid ${if (active) Theme.primary else Theme.border}")
            property("border-radius", "999px"); property("padding", "6px 14px")
            property("font-size", "13px"); property("cursor", "pointer")
        }
    }) { Text(text) }
}

/** Horizontal list row with a thumbnail, title/subtitle (click = primary action), and trailing actions. */
@Composable
fun RowItem(
    name: String,
    imageUrl: String?,
    subtitle: String?,
    onClick: () -> Unit,
    actions: @Composable () -> Unit,
) {
    Div(attrs = {
        style {
            cardStyle(); display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "12px")
            property("padding", "10px 12px"); property("margin-bottom", "10px")
        }
    }) {
        Div(attrs = {
            style {
                property("width", "64px"); property("height", "42px"); property("flex-shrink", "0")
                property("background", Theme.surfaceAlt); property("border-radius", "6px"); property("overflow", "hidden")
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            }
        }) {
            if (!imageUrl.isNullOrBlank()) {
                Img(src = proxied(imageUrl), attrs = { attr("loading", "lazy"); attr("decoding", "async"); style { property("width", "100%"); property("height", "100%"); property("object-fit", "cover") } })
            } else {
                Span(attrs = { style { property("color", Theme.textDim) } }) { Text(name.take(1).uppercase()) }
            }
        }
        Div(attrs = { onClick { onClick() }; style { property("flex-grow", "1"); property("cursor", "pointer"); property("min-width", "0") } }) {
            Div(attrs = { style { property("font-size", "14px"); property("font-weight", "600"); ellipsis() } }) { Text(name) }
            if (!subtitle.isNullOrBlank()) {
                Div(attrs = { style { property("font-size", "12px"); property("color", Theme.textDim); ellipsis() } }) { Text(subtitle) }
            }
        }
        actions()
    }
}
